import React, { useState } from 'react';
import { Container, Row, Col, Button, Image, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { generateCodeVerifier, generateCodeChallenge, generateState, PKCE_KEYS } from '../utils/pkce';

/**
 * LoginPage
 *
 * Per the integration guide, the username/password form is removed.
 * Authentication is handled entirely by Moodle (password + 2FA).
 *
 * Flow:
 *  1. User clicks "Log in with Moodle".
 *  2. PKCE values and a CSRF state token are generated and stored in localStorage.
 *  3. Browser is redirected to Moodle's authorization endpoint.
 *  4. Moodle handles login + 2FA + consent, then redirects back to /auth/callback.
 *  5. AuthCallback.jsx completes the session via the Python backend.
 */
const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const handleLogin = async () => {
    setLoading(true);
    try {
      // Generate fresh PKCE values for this login attempt
      const codeVerifier  = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state         = generateState();

      // Store in localStorage — needed when Moodle redirects back
      // (localStorage survives server redirects that can wipe sessionStorage)
      localStorage.setItem(PKCE_KEYS.CODE_VERIFIER, codeVerifier);
      localStorage.setItem(PKCE_KEYS.STATE, state);

      // Build Moodle authorization URL
      const params = new URLSearchParams({
        client_id             : process.env.REACT_APP_OAUTH_CLIENT_ID,
        redirect_uri          : `${window.location.origin}/auth/callback`,
        response_type         : 'code',
        state,
        code_challenge        : codeChallenge,
        code_challenge_method : 'S256',
      });

      // Redirect — browser navigates away, no further code runs here
      window.location.href =
        `https://${process.env.REACT_APP_LMS_URL}/local/reactauth/authorize.php?${params.toString()}`;
    } catch (err) {
      console.error('[LoginPage] Failed to initiate login:', err);
      toast.error('Could not start login. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section className="login-wrapper">
      <Container fluid>
        <Row>
          <Col xs="12" lg="6" className="d-none d-lg-block login-bg-wrapper">
            <div className="login-bg-img"></div>
          </Col>
          <Col xs="12" lg="6">
            <div className="login-form-container flex-1">
              <div>
                <div className="login-block">
                  <div className="text-start">
                    <Image src="/images/Svhs-logo.png" alt="SVHS logo" />
                  </div>
                  <div className="mt-4">
                    <p className="title-text">Sign In</p>
                    <p className="text-muted mb-4" style={{ fontSize: '0.95rem', textAlign: 'left' }}>
                      You will be redirected to the SVHS Learning Portal to sign in
                      securely with your credentials and two-factor authentication.
                    </p>
                    <Button
                      variant="primary"
                      className="w-100"
                      onClick={handleLogin}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="grow"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Redirecting…
                        </>
                      ) : (
                        'Log in with Moodle'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bottom-content mb-4 d-flex justify-content-between align-items-center mt-auto">
                <div className="external-link">
                  <Button variant="link" className="p-0">
                    Privacy Policy
                  </Button>
                </div>
                <p className="copyright-text mb-0">Copyright © {currentYear}</p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default LoginPage;
